import React, { useEffect, useMemo, useState } from 'react';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import { ComponentGroup } from '../ComponentGroups';
import { SequenceViewer } from '../SequenceViewer';
import { KeyboardInput } from '../KeyboardInput';
import { PersonalisationLoader } from '../PersonalisationLoader';
import { usePersonalisationContext } from '../../context/PersonalisationContext';

import tabsStyles from './personalisation-tabs.module.css';
import 'react-tabs/style/react-tabs.css';
import { logError } from '../../utilities/logging';

/**
 * PersonalisationComponentSlot component displays a component slot in the personalisation journey.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.slots - The slot object.
 * @param {Object} props.componentGroups - The component groups object.
 * @returns {import('react').JSX.Element | null}
 */
export default function PersonalisationSequentialSlot({ slots, componentGroups }) {
  const { variants } = usePersonalisationContext();
  const [currentSlotId, setCurrentSlotId] = useState(slots.find((slot) => slot.slotType === 'COMPONENT').id);
  const [currentSlot, setCurrentSlot] = useState();
  const [isAdvancing, setIsAdvancing] = useState(false);

  useEffect(() => {
    setCurrentSlot(slots.find((slot) => slot.id === currentSlotId));
  }, [currentSlotId, slots]);

  const { allowedComponentGroups = [], id: slotId = null } = currentSlot || {};

  const groups = useMemo(() => {
    if (!Array.isArray(allowedComponentGroups)) return [];

    const allGroups = allowedComponentGroups
      // Replace the IDs with the actual items.
      .map((groupId) => componentGroups.get(groupId))
      .filter((group) => {
        if (!group) return false;

        // Check if group has any valid variants
        const hasValidVariants = group.productVariantIds.map((id) => variants.get(id)).some((variant) => variant);

        return hasValidVariants;
      });

    // Separate single-item groups from multi-item groups
    const multiItemGroups = allGroups.filter((group) => group.productVariantIds.length > 1);
    const singleItemGroups = allGroups.filter((group) => group.productVariantIds.length === 1);

    // Create single item group if there are single-item groups
    // Don't assign price modifiers - let the original groups handle pricing
    const singleItemGroup =
      singleItemGroups.length > 0
        ? {
            id: 'other',
            uiHeading: 'Charms',
            productVariantIds: singleItemGroups.flatMap((group) => group.productVariantIds),
            defaultSelectionLabel: 'Choose',
            handle: 'other',
            internalName: 'Other',
            isTextGroup: false
          }
        : null;

    // Combine all groups
    const allCombinedGroups = singleItemGroup ? [...multiItemGroups, singleItemGroup] : multiItemGroups;

    // Sort groups to prioritize keyboard groups (isTextGroup: true) first
    return allCombinedGroups.sort((a, b) => {
      const aIsKeyboard = a.isTextGroup === true;
      const bIsKeyboard = b.isTextGroup === true;

      if (aIsKeyboard && !bIsKeyboard) return -1;
      if (!aIsKeyboard && bIsKeyboard) return 1;
      return 0;
    });
  }, [allowedComponentGroups, componentGroups, variants]);

  /**
   * Checks if the keyboard component should be shown for this group.
   * @param {Object} group - The component group to check.
   * @returns {boolean} - True if isTextGroup is true, all variants have asciiSymbol and are in stock, false otherwise.
   */

  const showKeyboard = (group) => {
    if (!group?.productVariantIds) return false;

    // Check if isTextGroup is true.
    if (group.isTextGroup !== true) return false;

    // Get all variants for this group using the variants context.
    const groupVariants = group.productVariantIds.map((id) => variants.get(id)).filter(Boolean);

    // Check if all variants have asciiSymbol set and are in stock.
    return groupVariants.every(
      (variant) => variant.asciiSymbol && variant.asciiSymbol.trim() !== '' && variant.availableForSale === true
    );
  };

  if (!currentSlot) return null;

  if (!Array.isArray(groups) || groups.length === 0) return null;

  if (!slotId) {
    logError({
      label: 'PersonalisationSequentialSlot',
      error: new Error('Slot ID is null'),
      message: 'Slot ID is null',
      extraData: {
        slotId,
        currentSlot
      }
    });
    return null;
  }

  return (
    <div>
      <PersonalisationLoader isVisible={isAdvancing} />
      <Tabs className={tabsStyles['personalisation__tabs']}>
        <div className={tabsStyles['personalisation__tabs-header']}>
          <SequenceViewer
            setCurrentSlotId={setCurrentSlotId}
            currentSlotId={currentSlotId}
            onAdvancingChange={setIsAdvancing}
          />
          <TabList className={tabsStyles['personalisation__tabs-list']}>
            {/* View All Tab */}
            <Tab
              key={'viewAll' + slotId + 'tabList'}
              selectedClassName={tabsStyles['personalisation__tabs-list-item--selected']}
              className={tabsStyles['personalisation__tabs-list-item'] + ' body-xs body-uppercase font-medium'}
            >
              View All
            </Tab>
            {/* Group Tabs Map */}
            {groups.map((group) => (
              <Tab
                key={group.id + slotId + 'tabList'}
                selectedClassName={tabsStyles['personalisation__tabs-list-item--selected']}
                className={tabsStyles['personalisation__tabs-list-item'] + ' body-xs body-uppercase font-medium'}
              >
                {group.uiHeading}
              </Tab>
            ))}
          </TabList>
        </div>
        {/* View All Tab Panel */}
        {groups.length > 1 && (
          <TabPanel
            className={
              tabsStyles['personalisation__tabs-panel'] + ' ' + tabsStyles['personalisation__tabs-panel--view-all']
            }
            key={'viewAll' + slotId + 'tabPanel'}
          >
            <div className={tabsStyles['personalisation__tabs-panel-content']}>
              {groups
                .filter((group) => showKeyboard(group))
                .map((group) => (
                  <div className={tabsStyles['personalisation__tabs-panel-group']} key={group.id + slotId + 'tabPanel'}>
                    <KeyboardInput group={group} slotId={slotId} maxLength={slots.length} />
                  </div>
                ))}
              {groups
                .filter((group) => !showKeyboard(group))
                .map((group) => (
                  <div className={tabsStyles['personalisation__tabs-panel-group']} key={group.id + slotId + 'tabPanel'}>
                    <ComponentGroup slot={currentSlot} group={group} slotId={slotId} showUiHeading={true} />
                  </div>
                ))}
            </div>
          </TabPanel>
        )}
        {/* Group Tab Panels Map */}
        {groups.map((group) => (
          <TabPanel className={tabsStyles['personalisation__tabs-panel']} key={group.id + slotId + 'tabPanel'}>
            <div className={tabsStyles['personalisation__tabs-panel-content']}>
              {showKeyboard(group) ? (
                <KeyboardInput group={group} slotId={slotId} maxLength={slots.length} />
              ) : (
                <ComponentGroup slot={currentSlot} group={group} slotId={slotId} showUiHeading={true} />
              )}
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
